import useGetUserOrganization from 'hooks/useGetUserOrganization';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from 'store';
import { notificationSlice } from 'store/slices';
import {
  getLastSync
} from 'store/slices/integrations/jamf/sync';
import {
  selectJamfSync,
  selectJamfSyncCreatedAt,
  selectJamfSyncedCount,
  selectJamfSyncGetLastSyncLoading,
  selectJamfSyncInitialized,
  selectJamfSyncLoadingState,
  selectJamfSyncProcessing,
  selectJamfUnsyncedCount
} from 'store/slices/integrations/jamf/sync/selectors';
import { selectJamfSettingsPath } from 'store/slices/integrations/list/selectors';

export const useHandleSettingsClick = () => {
  const navigate = useNavigate();
  const path = useSelector(selectJamfSettingsPath);
  
  return React.useCallback(() => {
    navigate(path);
  }, [navigate, path]);
};

export const useHandleErrorNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const getLastSyncLoading = useSelector(selectJamfSyncGetLastSyncLoading);

  React.useEffect(() => {
    if (getLastSyncLoading === 'rejected') {
      dispatch(
        notificationSlice.actions.setNotice({
          showNotice: true,
          noticeContent: 'Last sync not found',
        })
      );
    }
  }, [dispatch, getLastSyncLoading]);
};

export const useHandleSuccessNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const getLastSyncLoading = useSelector(selectJamfSyncGetLastSyncLoading);
  const isProcessing = useSelector(selectJamfSyncProcessing);

  React.useEffect(() => {
    if (getLastSyncLoading === 'fulfilled' && isProcessing) {
      dispatch(
        notificationSlice.actions.setNotice({
          showNotice: true,
          noticeContent: 'Sync in progress',
        })
      );
    }
  }, [dispatch, getLastSyncLoading, isProcessing]);
};

export const useGetJamfLastSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const org = useGetUserOrganization();
  
  const syncData = useSelector(selectJamfSync);
  const syncedCount = useSelector(selectJamfSyncedCount);
  const unsyncedCount = useSelector(selectJamfUnsyncedCount);
  const lastSyncDate = useSelector(selectJamfSyncCreatedAt);
  const isProcessing = useSelector(selectJamfSyncProcessing);
  const isInitialized = useSelector(selectJamfSyncInitialized);
  const loadingState = useSelector(selectJamfSyncLoadingState);
  
  const fetchSyncData = useCallback(() => {
    if (org?.id) {
      dispatch(getLastSync(org.id));
    }
  }, [dispatch, org?.id]);
  
  useEffect(() => {
    fetchSyncData();
  }, [fetchSyncData]);
  
  return {
    syncData,
    syncedCount,
    unsyncedCount,
    lastSyncDate,
    isProcessing,
    isInitialized,
    isLoading: loadingState === 'pending',
    hasError: loadingState === 'rejected',
    fetchSyncData
  };
};
